import { MOSIDA_API_BASE_URL } from "../config/apiUrls";
import { fetchImage } from "./imageReadService";

/** Mosida media CDN (same host used for product/pack assets). */
const DEFAULT_MEDIA_CDN = "https://d2s4ngnid78ki4.cloudfront.net";

export interface TemporaryUploadResult {
  success: boolean;
  url?: string;
  /** Raw S3 URL from upload response when available. */
  s3Url?: string;
  error?: string;
  data?: any;
}

function isHttpUrl(value: string): boolean {
  return /^https?:\/\//i.test((value || "").trim());
}

function isDataOrBlobUrl(value: string): boolean {
  return /^(data:|blob:)/i.test((value || "").trim());
}

function getMediaCdnBase(): string {
  const fromEnv = (
    process.env.REACT_APP_CDN_DOMAIN ||
    process.env.REACT_APP_CLOUDFRONT_DOMAIN ||
    ""
  ).trim();
  return (fromEnv || DEFAULT_MEDIA_CDN).replace(/\/+$/, "");
}

/**
 * Convert Mosida S3 URLs / object keys to the public CloudFront CDN URL.
 * Matches mosida-api `toCdnUrl` behavior so Google AI can fetch the image.
 */
export function toCdnMediaUrl(url?: string | null): string {
  const trimmed = (url || "").trim();
  if (!trimmed) return "";

  const cdnBase = getMediaCdnBase();
  if (!cdnBase) return trimmed;

  if (trimmed.startsWith(cdnBase)) return trimmed;
  if (/cloudfront\.net/i.test(trimmed)) return trimmed;

  if (trimmed.includes("amazonaws.com")) {
    try {
      const parsed = new URL(trimmed);
      const key = parsed.pathname.startsWith("/")
        ? parsed.pathname.slice(1)
        : parsed.pathname;
      return key ? `${cdnBase}/${decodeURIComponent(key)}` : trimmed;
    } catch {
      return trimmed;
    }
  }

  // Relative S3 object key (e.g. main/creative/background-removal/uuid.png)
  if (!/^https?:\/\//i.test(trimmed) && !isDataOrBlobUrl(trimmed)) {
    const key = trimmed.replace(/^\/+/, "");
    return key ? `${cdnBase}/${key}` : trimmed;
  }

  return trimmed;
}

function extensionForMime(mime: string): string {
  if (mime.includes("jpeg") || mime.includes("jpg")) return "jpg";
  if (mime.includes("webp")) return "webp";
  if (mime.includes("gif")) return "gif";
  return "png";
}

async function sourceToUploadFile(
  source: string | Blob | File,
  preferredName = `group-selfie-${Date.now()}`
): Promise<File> {
  if (source instanceof File) {
    const type = source.type || "image/png";
    const ext = extensionForMime(type);
    const base = (source.name || preferredName).replace(/\.\w+$/, "");
    return new File([source], `${base}.${ext}`, { type });
  }
  if (source instanceof Blob) {
    const type = source.type || "image/png";
    const ext = extensionForMime(type);
    return new File([source], `${preferredName}.${ext}`, { type });
  }

  const response = await fetchImage(source);
  if (!response.ok) {
    throw new Error(`Failed to read image for upload: ${response.status}`);
  }
  const blob = await response.blob();
  // Preserve PNG for transparent art: data URLs / empty MIME must not become JPEG.
  const dataMimeMatch =
    typeof source === "string" ? source.match(/^data:(image\/[a-zA-Z0-9.+-]+);/i) : null;
  const type = blob.type || dataMimeMatch?.[1] || "image/png";
  const ext = extensionForMime(type);
  return new File([blob], `${preferredName}.${ext}`, { type });
}

/**
 * Upload an image to Mosida temporary S3 storage and return the CDN URL.
 * POST `${MOSIDA_API_BASE_URL}/file-upload/upload-temporary`
 */
export async function uploadTemporaryImage(
  source: string | Blob | File
): Promise<TemporaryUploadResult> {
  const baseUrl = MOSIDA_API_BASE_URL.replace(/\/+$/, "");
  if (!baseUrl) {
    return { success: false, error: "Mosida API base URL is not configured." };
  }

  const apiKey = process.env.REACT_APP_CREATIVE_API_KEY || "";
  if (!apiKey) {
    return { success: false, error: "REACT_APP_CREATIVE_API_KEY is not set." };
  }

  try {
    const file = await sourceToUploadFile(source);
    const form = new FormData();
    form.append("file", file, file.name || "group-selfie.png");

    const response = await fetch(`${baseUrl}/file-upload/upload-temporary`, {
      method: "POST",
      headers: {
        accept: "application/json",
        "x-api-key": apiKey,
      },
      body: form,
    });

    const rawText = await response.text();
    let json: any = null;
    try {
      json = rawText ? JSON.parse(rawText) : {};
    } catch {
      json = { raw: rawText };
    }

    if (!response.ok) {
      return {
        success: false,
        data: json,
        error:
          json?.message ||
          json?.error ||
          `Temporary upload failed: ${response.status} ${response.statusText}`,
      };
    }

    const data = json?.data ?? json;
    const rawUrl =
      (typeof data?.cdnUrl === "string" && data.cdnUrl.trim()) ||
      (typeof data?.cdn_url === "string" && data.cdn_url.trim()) ||
      (typeof data?.mainUrl === "string" && data.mainUrl.trim()) ||
      (typeof data?.main_url === "string" && data.main_url.trim()) ||
      (typeof data?.url === "string" && data.url.trim()) ||
      (typeof data?.imageUrl === "string" && data.imageUrl.trim()) ||
      (typeof data?.image_url === "string" && data.image_url.trim()) ||
      "";

    if (!rawUrl) {
      return {
        success: false,
        data: json,
        error: "Upload succeeded but no image URL was returned.",
      };
    }

    const cdnUrl = toCdnMediaUrl(rawUrl);
    return {
      success: true,
      url: cdnUrl,
      s3Url: rawUrl.includes("amazonaws.com") ? rawUrl : undefined,
      data: json,
    };
  } catch (e: any) {
    return {
      success: false,
      error: e?.message || "Network error while uploading image to S3.",
    };
  }
}

/**
 * Resolve a public CDN URL for Google AI:
 * - http(s) sources are normalized to CDN when they are S3 URLs
 * - data/blob/local sources are uploaded first, then converted to CDN
 */
export async function resolvePublicImageUrl(
  source: string
): Promise<TemporaryUploadResult> {
  const trimmed = (source || "").trim();
  if (!trimmed) {
    return { success: false, error: "Missing image source." };
  }

  if (isHttpUrl(trimmed) && !isDataOrBlobUrl(trimmed)) {
    const cdnUrl = toCdnMediaUrl(trimmed);
    return { success: true, url: cdnUrl, s3Url: trimmed.includes("amazonaws.com") ? trimmed : undefined };
  }

  return uploadTemporaryImage(trimmed);
}
